import { type Dispatch, type SetStateAction } from "react";
import {
  type Control,
  Controller,
  useController,
  useWatch,
} from "react-hook-form";

import {
  useCustomFieldsAndGroups,
  useWorkspaceConnections,
} from "@web-app/api";
import {
  CurrencyInput,
  CustomDropdownField,
  CustomNumberField,
  CustomTextField,
  DateInput,
  ErrorMessage,
  Field,
  GoogleMapAutoComplete,
  Input,
  InputWithAddressAutoComplete,
  Label,
  PhoneInput,
  RadioBoxWithSelect,
  SelectInput,
  TextArea,
  validatePhone,
} from "@web-app/components";
import useDispatchStore from "@web-app/store/dispatchStore";
import {
  CustomField,
  CustomFieldValue,
  GoogleAddress,
  OrderDeliveryType,
  OrderPriorityType,
  SavedAddress,
  TransportType,
} from "@web-app/types";
import { timeZoneMap } from "@web-app/utils/helpers";
import { errorMessages } from "@web-app/utils/order";

import {
  extractCustomFieldId,
  isCustomField,
  isSystemField,
} from "../../../../settings/FormBuilderTab/constants";
import type {
  DetailsType,
  OrderFormField,
} from "../../../../settings/FormBuilderTab/types";
import { FormValues } from "../../types";
import ItemChecklistField from "../ItemChecklistField";
import { ScannableCodeDetailsField } from "../ScannableCodeDetailsField";

interface ConfigDrivenTabProps {
  control: Control<FormValues>;
  currentWorkspaceId: string;
  customFieldErrors?: Record<string, string>;
  customFieldsState: Array<{
    definition: CustomField;
    values: CustomFieldValue[];
  }>;
  deliveryType: OrderDeliveryType;
  detailsType: DetailsType;
  dropoffLocation: GoogleAddress | null;
  fields: OrderFormField[];
  handleAddressSelect: (address: SavedAddress, isPickup: boolean) => void;
  onAddressSelect: (address: GoogleAddress | null, isPickup: boolean) => void;
  onPriorityChange: (value: OrderPriorityType) => void;
  onSaveDropoffDetailsChange: (value: boolean) => void;
  onSavePickupDetailsChange: (value: boolean) => void;
  pickupLocation: GoogleAddress | null;
  priority: OrderPriorityType;
  saveDropoffDetails: boolean;
  savePickupDetails: boolean;
  setCustomFields: Dispatch<
    SetStateAction<
      Array<{ definition: CustomField; values: CustomFieldValue[] }>
    >
  >;
  setWorkspaceOptionType: (value: string) => void;
  workspaceOptionType: string;
}

export function ConfigDrivenTab({
  control,
  currentWorkspaceId,
  customFieldErrors,
  customFieldsState,
  deliveryType,
  detailsType,
  dropoffLocation,
  fields,
  handleAddressSelect,
  onAddressSelect,
  onPriorityChange,
  onSaveDropoffDetailsChange,
  onSavePickupDetailsChange,
  pickupLocation,
  priority,
  saveDropoffDetails,
  savePickupDetails,
  setCustomFields,
  setWorkspaceOptionType,
  workspaceOptionType,
}: ConfigDrivenTabProps) {
  const isPickup = detailsType === "pickup";
  const isIndividualDelivery =
    deliveryType === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF;

  const timezone = useWatch({ control, name: "timezone" }) as
    keyof typeof timeZoneMap | undefined;
  const { field: currencyField } = useController({
    control,
    name: "currency",
  });
  const watchedCurrency = useWatch({ control, name: "currency" }) ?? "USD";

  const { data: workspaceConnections } = useWorkspaceConnections();
  const { user } = useDispatchStore();
  const currentWorkspaceName = user?.currentWorkspace?.name || "";

  const [{ data: customFields }] = useCustomFieldsAndGroups();

  const sortedFields = [...fields].sort((a, b) => a.position - b.position);

  // --- Custom field helpers ---

  const handleCustomFieldChange = (
    _fieldId: string,
    customField: CustomField,
    value: CustomFieldValue[],
  ) => {
    const existingIndex = customFieldsState.findIndex(
      (cf) => cf.definition.id === customField.id,
    );
    const updated = structuredClone(customFieldsState);
    if (existingIndex > -1) {
      updated[existingIndex] = { definition: customField, values: value };
    } else {
      updated.push({ definition: customField, values: value });
    }
    setCustomFields(updated);
  };

  const renderCustomFieldInline = (field: OrderFormField) => {
    const cfId = extractCustomFieldId(field.fieldKey);
    const cfDef = customFields?.find((cf) => cf.id === cfId);
    if (!cfDef) return null;

    const stateEntry = customFieldsState.find((s) => s.definition.id === cfId);
    const fieldValues =
      stateEntry?.values && stateEntry.values.length > 0
        ? stateEntry.values
        : (cfDef.defaultValues ?? []);

    switch (cfDef.type) {
      case "TEXT":
        return (
          <CustomTextField
            fieldId={`field-id-${cfDef.name}`}
            customField={cfDef}
            fieldValues={fieldValues}
            isReadOnly={false}
            onChange={handleCustomFieldChange}
            isRequired={field.isRequired}
            error={customFieldErrors?.[cfId]}
          />
        );
      case "NUMBER":
        return (
          <CustomNumberField
            fieldId={`field-id-${cfDef.name}`}
            customField={cfDef}
            fieldValues={fieldValues}
            isReadOnly={false}
            onChange={handleCustomFieldChange}
            isRequired={field.isRequired}
            error={customFieldErrors?.[cfId]}
          />
        );
      case "DROPDOWN":
        return (
          <CustomDropdownField
            fieldId={`field-id-${cfDef.name}`}
            customField={cfDef}
            fieldValues={fieldValues}
            isReadOnly={false}
            onChange={handleCustomFieldChange}
            isRequired={field.isRequired}
            error={customFieldErrors?.[cfId]}
          />
        );
      default:
        return null;
    }
  };

  // --- System field renderers ---

  const renderFullNameField = (field: OrderFormField) => {
    const formFieldName = isPickup
      ? ("pickupName" as const)
      : ("dropOffName" as const);
    const errorMsg = isPickup
      ? "Full name is required"
      : errorMessages.dropoff.dropOffName;
    return (
      <Field>
        <Label required={field.isRequired}>Full Name</Label>
        <Controller
          name={formFieldName}
          control={control}
          rules={{
            required: field.isRequired ? errorMsg : false,
          }}
          render={({ field: f, fieldState: { error } }) => (
            <>
              <InputWithAddressAutoComplete
                type="text"
                placeholder="Enter full name"
                currentWorkspaceId={currentWorkspaceId}
                onClickAddress={(address: { name: string }) =>
                  f.onChange(address.name)
                }
                onAddressSelect={async (address) => {
                  await handleAddressSelect(address, isPickup);
                }}
                {...f}
                invalid={!!error}
              />
              {error && <ErrorMessage message={error.message} />}
            </>
          )}
        />
      </Field>
    );
  };

  const renderPhoneField = (field: OrderFormField) => {
    const formFieldName = isPickup
      ? ("pickupPhone" as const)
      : ("dropOffPhone" as const);
    return (
      <Field>
        <Label required={field.isRequired}>Phone Number</Label>
        <Controller
          name={formFieldName}
          control={control}
          rules={{
            required: field.isRequired ? "Phone number is required" : false,
            validate: (value: string) => {
              if (!value) return;
              if (!validatePhone(value)) {
                return "Phone number must be in E.164 format (e.g., +1234567890)";
              }
            },
          }}
          render={({ field: f, fieldState: { error } }) => (
            <>
              <PhoneInput
                {...f}
                onChange={(value: string) => f.onChange(value)}
                defaultValue={f.value ? f.value : ""}
                invalid={!!error}
              />
              {error && <ErrorMessage message={error.message} />}
            </>
          )}
        />
      </Field>
    );
  };

  const renderAddressField = (field: OrderFormField) => {
    const formFieldName = isPickup
      ? ("pickupLocation" as const)
      : ("dropoffLocation" as const);
    const location = isPickup ? pickupLocation : dropoffLocation;
    const placeholder = isPickup
      ? "Enter pickup location"
      : "Enter drop-off location";
    return (
      <Field>
        <Label required={field.isRequired}>Address</Label>
        <Controller
          name={formFieldName}
          control={control}
          rules={{
            required: field.isRequired
              ? `${isPickup ? "Pickup" : "Drop-off"} location is required`
              : false,
          }}
          render={({ fieldState: { error }, field: { onChange } }) => (
            <>
              <GoogleMapAutoComplete
                address={location}
                setAddress={(address) => {
                  onAddressSelect(address, isPickup);
                  onChange(address);
                }}
                placeholder={placeholder}
                required={field.isRequired}
                invalid={!!error}
              />
              {error && <ErrorMessage message={error.message} />}
            </>
          )}
        />
      </Field>
    );
  };

  const renderPickupAtField = (field: OrderFormField) => {
    const startTimeField = (
      <Field>
        <Label required={field.isRequired}>
          {isIndividualDelivery
            ? "Pickup At (Start Time)"
            : "Pickup After (Date/Time)"}
        </Label>
        <Controller
          name="scheduledStartDateTime"
          control={control}
          rules={{
            required: field.isRequired ? "Start time is required" : false,
          }}
          render={({ field: { onChange, ...rest }, fieldState: { error } }) => (
            <>
              <DateInput
                minDate={new Date()}
                {...{
                  onChange: (date) => onChange(date),
                  ...rest,
                  required: field.isRequired,
                }}
                invalid={!!error}
                timezone={timezone}
              />
              {error && <ErrorMessage message={error.message} />}
            </>
          )}
        />
      </Field>
    );

    if (isIndividualDelivery) return startTimeField;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {startTimeField}
        <Field>
          <Label required={field.isRequired}>Pickup Before (Date/Time)</Label>
          <Controller
            name="pickupEndTime"
            control={control}
            rules={{
              required: field.isRequired ? "End time is required" : false,
            }}
            render={({
              field: { onChange, ...rest },
              fieldState: { error },
            }) => (
              <>
                <DateInput
                  minDate={new Date()}
                  {...{
                    onChange: (date) => onChange(date),
                    ...rest,
                    required: field.isRequired,
                  }}
                  invalid={!!error}
                  timezone={timezone}
                />
                {error && <ErrorMessage message={error.message} />}
              </>
            )}
          />
        </Field>
      </div>
    );
  };

  const renderDropoffAtField = (field: OrderFormField) => {
    const endTimeField = (
      <Field>
        <Label required={field.isRequired}>Drop-off At (End Time)</Label>
        <Controller
          name="scheduledEndDateTime"
          control={control}
          rules={{
            required: field.isRequired ? "End time is required" : false,
          }}
          render={({ field: { onChange, ...rest }, fieldState: { error } }) => (
            <>
              <DateInput
                minDate={new Date()}
                onChange={(date) => onChange(date)}
                {...rest}
                invalid={!!error}
                timezone={timezone}
              />
              {error && <ErrorMessage message={error.message} />}
            </>
          )}
        />
      </Field>
    );

    if (isIndividualDelivery) return endTimeField;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field>
          <Label required={field.isRequired}>Drop-off After (Date/Time)</Label>
          <Controller
            name="dropOffStartTime"
            control={control}
            rules={{
              required: field.isRequired ? "Start time is required" : false,
            }}
            render={({
              field: { onChange, value, ...rest },
              fieldState: { error },
            }) => (
              <>
                <DateInput
                  minDate={new Date()}
                  value={value instanceof Date ? value : new Date()}
                  onChange={onChange}
                  {...rest}
                  invalid={!!error}
                  timezone={timezone}
                />
                {error && <ErrorMessage message={error.message} />}
              </>
            )}
          />
        </Field>
        {endTimeField}
      </div>
    );
  };

  const renderBusinessNameField = (field: OrderFormField) => {
    const formFieldName = isPickup
      ? ("pickupBusinessName" as const)
      : ("dropOffBusinessName" as const);
    return (
      <Field>
        <Label required={field.isRequired}>Business Name</Label>
        <Controller
          name={formFieldName}
          control={control}
          rules={{
            required: field.isRequired ? "Business name is required" : false,
          }}
          render={({ field: f, fieldState: { error } }) => (
            <>
              <Input
                type="text"
                placeholder="Enter business name"
                {...f}
                invalid={!!error}
              />
              {error && <ErrorMessage message={error.message} />}
            </>
          )}
        />
      </Field>
    );
  };

  const renderServiceTimeField = (field: OrderFormField) => {
    const formFieldName = isPickup
      ? ("pickupServiceTime" as const)
      : ("dropOffServiceTime" as const);
    return (
      <Field>
        <Label required={field.isRequired}>Service Time (minutes)</Label>
        <Controller
          name={formFieldName}
          control={control}
          rules={{
            min: {
              message: "Service time must be a positive number",
              value: 0,
            },
            required: field.isRequired ? "Service time is required" : false,
          }}
          render={({ field: f, fieldState: { error } }) => (
            <>
              <Input
                type="number"
                placeholder="Enter service time"
                {...f}
                invalid={!!error}
              />
              {error && <ErrorMessage message={error.message} />}
            </>
          )}
        />
      </Field>
    );
  };

  const renderOrderRequirementsField = (field: OrderFormField) => {
    const prefix = isPickup
      ? "pickupOrderRequirements"
      : "dropoffOrderRequirements";

    return (
      <div className="space-y-2">
        <Label required={field.isRequired}>Order Requirements</Label>
        <div className="flex flex-col sm:flex-row flex-wrap gap-x-4 gap-y-1 sm:gap-y-2">
          <Controller
            name={`${prefix}_SIGNATURE` as keyof FormValues}
            control={control}
            render={({ field: f }) => (
              <Input
                type="checkbox"
                rightLabel="Signature Required"
                value={String(f.value)}
                onChange={(value: string) => f.onChange(value === "true")}
              />
            )}
          />
          <Controller
            name={`${prefix}_PHOTO` as keyof FormValues}
            control={control}
            render={({ field: f }) => (
              <Input
                type="checkbox"
                rightLabel="Photo Required"
                value={String(f.value)}
                onChange={(value: string) => f.onChange(value === "true")}
              />
            )}
          />
          <Controller
            name={`${prefix}_NOTES` as keyof FormValues}
            control={control}
            render={({ field: f }) => (
              <Input
                type="checkbox"
                rightLabel="Notes Required"
                value={String(f.value)}
                onChange={(value: string) => f.onChange(value === "true")}
              />
            )}
          />
          <Controller
            name={`${prefix}_ITEMCHECKLIST` as keyof FormValues}
            control={control}
            render={({ field: f }) => (
              <Input
                type="checkbox"
                rightLabel="Item Checklist Required"
                value={String(f.value)}
                onChange={(value: string) => f.onChange(value === "true")}
              />
            )}
          />
        </div>
      </div>
    );
  };

  const renderNoteField = (field: OrderFormField) => {
    const formFieldName: keyof FormValues = isPickup
      ? "pickupNote"
      : "dropOffNote";
    const placeholder = isPickup ? "Enter pickup note" : "Enter drop-off note";

    return (
      <Field>
        <Label required={field.isRequired}>Note</Label>
        <Controller
          name={formFieldName}
          control={control}
          render={({ field: f }) => (
            <TextArea placeholder={placeholder} rows={3} {...f} />
          )}
        />
      </Field>
    );
  };

  const renderSaveDetailsField = () => {
    const formFieldName: keyof FormValues = isPickup
      ? "savePickupDetails"
      : "saveDropoffDetails";
    const saveValue = isPickup ? savePickupDetails : saveDropoffDetails;
    const onChangeHandler = isPickup
      ? onSavePickupDetailsChange
      : onSaveDropoffDetailsChange;

    return (
      <Field>
        <Controller
          name={formFieldName}
          control={control}
          render={() => (
            <Input
              type="checkbox"
              rightLabel="Save details to Address Book"
              value={saveValue.toString()}
              onChange={(value: string) => onChangeHandler(value === "true")}
            />
          )}
        />
      </Field>
    );
  };

  const renderDescriptionField = (field: OrderFormField) => (
    <Field>
      <Label required={field.isRequired}>Description</Label>
      <Controller
        name="description"
        control={control}
        rules={{
          required: field.isRequired ? errorMessages.order.description : false,
        }}
        render={({ field: f, fieldState: { error } }) => (
          <div>
            <TextArea
              id="description"
              placeholder="Enter order description"
              rows={3}
              {...f}
              invalid={!!error}
            />
            {error && <ErrorMessage message={error.message} />}
          </div>
        )}
      />
    </Field>
  );

  const renderTimezoneField = (field: OrderFormField) => {
    const timezoneOptions = Object.keys(timeZoneMap).map((t) => ({
      label: t,
      value: t,
    }));

    return (
      <Field>
        <Label required={field.isRequired}>Timezone</Label>
        <Controller
          name="timezone"
          control={control}
          rules={{
            required: field.isRequired ? "Timezone is required" : false,
          }}
          render={({ field: f, fieldState: { error } }) => (
            <div>
              <SelectInput
                options={timezoneOptions}
                placeholder="Select Timezone"
                selected={f.value}
                onChange={f.onChange}
                invalid={!!error}
                required={field.isRequired}
              />
              {error && <ErrorMessage message={error.message} />}
            </div>
          )}
        />
      </Field>
    );
  };

  const renderPriorityField = () => (
    <Field>
      <Label>Priority</Label>
      <Controller
        name="priority"
        control={control}
        render={() => (
          <Input
            type="checkbox"
            rightLabel="High Priority"
            value={(priority === OrderPriorityType.HIGH).toString()}
            onChange={(value: string) => {
              onPriorityChange(
                value === "true"
                  ? OrderPriorityType.HIGH
                  : OrderPriorityType.LOW,
              );
            }}
          />
        )}
      />
    </Field>
  );

  const renderExternalIdField = (field: OrderFormField) => (
    <Field>
      <Label required={field.isRequired}>External Reference ID</Label>
      <Controller
        name="externalId"
        control={control}
        render={({ field: f }) => (
          <Input type="text" placeholder="123" {...f} />
        )}
      />
    </Field>
  );

  const renderVehicleTypeField = (field: OrderFormField) => (
    <Field>
      <Label required={field.isRequired}>Mode of Transportation</Label>
      <Controller
        name="vehicleType"
        control={control}
        rules={{
          required: field.isRequired ? errorMessages.order.vehicleType : false,
        }}
        render={({ field: f, fieldState: { error } }) => (
          <div>
            <SelectInput
              options={Object.entries(TransportType).map(([key, value]) => ({
                label: key,
                value: value,
              }))}
              placeholder="Select Vehicle"
              selected={f.value}
              onChange={f.onChange}
              invalid={!!error}
            />
            {error && <ErrorMessage message={error.message} />}
          </div>
        )}
      />
    </Field>
  );

  const renderValueField = (field: OrderFormField) => (
    <Field>
      <Label required={field.isRequired}>Value</Label>
      <Controller
        name="value"
        control={control}
        rules={{
          required: field.isRequired ? errorMessages.order.value : false,
        }}
        render={({ field: valueField, fieldState: { error } }) => (
          <div>
            <CurrencyInput
              value={valueField.value}
              onChange={valueField.onChange}
              selectedCurrency={watchedCurrency}
              onCurrencyChange={currencyField.onChange}
              invalid={!!error}
            />
            {error && <ErrorMessage message={error.message} />}
          </div>
        )}
      />
    </Field>
  );

  const renderTipField = (field: OrderFormField) => (
    <Field>
      <Label required={field.isRequired}>Tip</Label>
      <Controller
        name="tip"
        control={control}
        rules={{
          required: field.isRequired ? errorMessages.order.tip : false,
        }}
        render={({ field: tipField, fieldState: { error } }) => (
          <div>
            <CurrencyInput
              value={tipField.value}
              onChange={tipField.onChange}
              selectedCurrency={watchedCurrency}
              onCurrencyChange={currencyField.onChange}
              invalid={!!error}
            />
            {error && <ErrorMessage message={error.message} />}
          </div>
        )}
      />
    </Field>
  );

  const renderItemsCountField = (field: OrderFormField) => (
    <Field>
      <Label required={field.isRequired}>Item Count</Label>
      <Controller
        name="itemsCount"
        control={control}
        rules={{
          required: field.isRequired ? errorMessages.order.itemsCount : false,
        }}
        render={({ field: f, fieldState: { error } }) => (
          <div>
            <Input
              type="number"
              min={0}
              placeholder="Enter number of items"
              {...f}
              invalid={!!error}
            />
            {error && <ErrorMessage message={error.message} />}
          </div>
        )}
      />
    </Field>
  );

  const renderDimensionField = (
    field: OrderFormField,
    formFieldName: "height" | "length" | "weight" | "width",
    label: string,
    placeholder: string,
  ) => (
    <Field>
      <Label required={field.isRequired}>{label}</Label>
      <Controller
        name={formFieldName}
        control={control}
        render={({ field: f }) => (
          <Input
            type="text"
            placeholder={placeholder}
            value={f.value ?? ""}
            onChange={f.onChange}
            onBlur={f.onBlur}
            name={f.name}
          />
        )}
      />
    </Field>
  );

  const renderDisableSMSField = () => (
    <Field>
      <Controller
        name="disableSMS"
        control={control}
        render={({ field: f }) => (
          <Input
            type="checkbox"
            rightLabel="Disable SMS notifications"
            onChange={(value: string) => f.onChange(value === "true")}
            value={f.value.toString()}
          />
        )}
      />
    </Field>
  );

  // The checklist belongs to a leg. Configs saved with it under "order" are
  // moved to the legs by normalizeItemChecklistFields.
  const renderItemChecklistField = (field: OrderFormField) =>
    detailsType === "order" ? null : (
      <ItemChecklistField
        control={control}
        name={isPickup ? "pickupChecklist" : "dropoffChecklist"}
        required={field.isRequired}
      />
    );

  const renderConnectedWorkspaceIdField = (field: OrderFormField) => {
    if (!workspaceConnections?.length) return null;

    return (
      <Field>
        <Label required={field.isRequired}>Connected Workspace Options</Label>
        <Controller
          name="connectedWorkspaceId"
          control={control}
          render={({ field: f }) => (
            <RadioBoxWithSelect
              value={workspaceOptionType}
              onChange={setWorkspaceOptionType}
              selectPlaceHolder={currentWorkspaceName}
              selectValue={f.value || ""}
              onSelectChange={f.onChange}
              options={[
                {
                  id: "1",
                  label: "Create on behalf of workspace",
                  tooltip:
                    "Enter or select the workspace that issued this order.",
                  value: "createFor",
                },
                {
                  id: "2",
                  label: "Send to workspace",
                  tooltip:
                    "Enter or select a workspace you wish to send this order to deliver.",
                  value: "sendTo",
                },
              ]}
              selectOptions={
                workspaceConnections
                  ?.filter(
                    (connection) =>
                      connection.connectedWorkspace.id !==
                      user?.currentWorkspaceId,
                  )
                  .map((connection) => ({
                    label: connection.connectedWorkspace.name,
                    value: connection.connectedWorkspace.id,
                  })) || []
              }
            />
          )}
        />
      </Field>
    );
  };

  // --- Field dispatch ---

  const renderSystemField = (field: OrderFormField) => {
    switch (field.fieldKey) {
      case "systemField_fullName":
        return renderFullNameField(field);
      case "systemField_phone":
        return renderPhoneField(field);
      case "systemField_address":
        return renderAddressField(field);
      case "systemField_pickupAt":
        return renderPickupAtField(field);
      case "systemField_dropoffAt":
        return renderDropoffAtField(field);
      case "systemField_businessName":
        return renderBusinessNameField(field);
      case "systemField_serviceTime":
        return renderServiceTimeField(field);
      case "systemField_orderRequirements":
        return renderOrderRequirementsField(field);
      case "systemField_note":
        return renderNoteField(field);
      case "systemField_saveDetails":
        return renderSaveDetailsField();
      case "systemField_description":
        return renderDescriptionField(field);
      case "systemField_timezone":
        return renderTimezoneField(field);
      case "systemField_priority":
        return renderPriorityField();
      case "systemField_externalId":
        return renderExternalIdField(field);
      case "systemField_vehicleType":
        return renderVehicleTypeField(field);
      case "systemField_value":
        return renderValueField(field);
      case "systemField_tip":
        return renderTipField(field);
      case "systemField_itemsCount":
        return renderItemsCountField(field);
      case "systemField_length":
        return renderDimensionField(field, "length", "Length (cm)", "50.25");
      case "systemField_width":
        return renderDimensionField(field, "width", "Width (cm)", "30.50");
      case "systemField_height":
        return renderDimensionField(field, "height", "Height (cm)", "10.00");
      case "systemField_weight":
        return renderDimensionField(field, "weight", "Weight (gm)", "500.00");
      case "systemField_disableSMS":
        return renderDisableSMSField();
      case "systemField_itemChecklist":
        return renderItemChecklistField(field);
      case "systemField_connectedWorkspaceId":
        return renderConnectedWorkspaceIdField(field);
      default:
        return null;
    }
  };

  const renderField = (field: OrderFormField) => {
    if (isSystemField(field.fieldKey)) {
      return renderSystemField(field);
    }
    if (isCustomField(field.fieldKey)) {
      return renderCustomFieldInline(field);
    }
    return null;
  };

  return (
    <div className="space-y-3 md:space-y-4 pb-10 md:pb-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sortedFields.map((field) => (
          <div
            key={`${field.fieldKey}-${field.position}`}
            className={
              field.colSpan === 2 ? "col-span-1 md:col-span-2" : "col-span-1"
            }
          >
            {renderField(field)}
          </div>
        ))}
      </div>
      {/* Not part of the form builder config yet, so always shown here. */}
      {detailsType !== "order" && (
        <ScannableCodeDetailsField
          control={control}
          name={detailsType === "pickup" ? "pickupCodes" : "dropoffCodes"}
        />
      )}
    </div>
  );
}
